import { Prestamo } from '../types';

/**
 * Préstamos pasados de fecha: los que siguen fuera después del día en que
 * quedasteis que volverían. Lo usan el aviso del Inicio, la lista de
 * Préstamos y el push diario (netlify/lib/recordatoriosPush.mts), así que el
 * texto y la cuenta de días son los mismos en todas partes.
 */

export type DatosPrestamo = Pick<
  Prestamo, 'objeto' | 'persona' | 'direccion' | 'fechaPrestamo' | 'fechaPrevista' | 'fechaDevolucion'
>;

/** Días tras el inicio de la época del día de `d`, en `zona` (o en la del dispositivo). */
function numeroDeDia(d: Date, zona?: string): number {
  if (!zona) return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / 86_400_000;
  const p = new Intl.DateTimeFormat('en-CA', { timeZone: zona, year: 'numeric', month: '2-digit', day: '2-digit' })
    .formatToParts(d);
  const v = (t: string) => Number(p.find(x => x.type === t)?.value);
  return Date.UTC(v('year'), v('month') - 1, v('day')) / 86_400_000;
}

/** Días que lleva pasado de fecha (0 si aún no ha vencido, ya volvió o no tiene fecha). */
export function diasDeRetraso(p: DatosPrestamo, hoy = new Date(), zona?: string): number {
  if (p.fechaDevolucion || !p.fechaPrevista) return 0;
  return Math.max(0, numeroDeDia(hoy, zona) - numeroDeDia(p.fechaPrevista, zona));
}

export const estaVencido = (p: DatosPrestamo, hoy = new Date(), zona?: string) =>
  diasDeRetraso(p, hoy, zona) > 0;

/** Los vencidos, del que lleva más retraso al que menos. */
export function prestamosVencidos<T extends DatosPrestamo>(lista: T[], hoy = new Date(), zona?: string): T[] {
  return lista
    .filter(p => estaVencido(p, hoy, zona))
    .sort((a, b) => diasDeRetraso(b, hoy, zona) - diasDeRetraso(a, hoy, zona));
}

const diaMes = (d: Date, zona?: string) =>
  d.toLocaleDateString('es-ES', { day: 'numeric', month: 'numeric', ...(zona ? { timeZone: zona } : {}) });

const hace = (n: number) => (n === 1 ? 'ayer' : `hace ${n} días`);

/**
 * Una frase que cuenta qué pasa, quién lo tiene y desde cuándo:
 * Paco tiene «Taladro»: tenía que volver el 26/10 (hace 3 días).
 */
export function fraseVencido(p: DatosPrestamo, hoy = new Date(), zona?: string): string {
  const cuando = `el ${diaMes(p.fechaPrevista!, zona)} (${hace(diasDeRetraso(p, hoy, zona))})`;
  return p.direccion === 'prestado'
    ? `${p.persona} tiene «${p.objeto}»: tenía que volver ${cuando}.`
    : `${p.persona} te dejó «${p.objeto}»: quedaste en devolverlo ${cuando}.`;
}

/** Título y cuerpo de la notificación; varios préstamos van en una sola. */
export function textoPushPrestamos(lista: DatosPrestamo[], hoy = new Date(), zona?: string) {
  if (lista.length === 1) {
    return { titulo: '📦 Préstamo pasado de fecha', cuerpo: fraseVencido(lista[0], hoy, zona) };
  }
  const nombres = lista.slice(0, 2).map(p => `${p.objeto} (${p.persona})`);
  const resto = lista.length - nombres.length;
  const cuales = resto > 0 ? `${nombres.join(', ')} y ${resto} más` : nombres.join(' y ');
  return { titulo: `📦 ${lista.length} préstamos pasados de fecha`, cuerpo: `${cuales}. ¿Ya han vuelto?` };
}

// ── «Más tarde», por dispositivo ─────────────────────────────────────────────
// Es una comodidad de quien mira, no un dato del préstamo: vale localStorage.

export const DIAS_POSPONER = 7;
const clavePospuesto = (id: string) => `rzcentral.prestamos.pospuesto.${id}`;

export function prestamoPospuestoHasta(id: string): number {
  try { return Number(localStorage.getItem(clavePospuesto(id))) || 0; } catch { return 0; }
}

export function posponerPrestamo(id: string, dias = DIAS_POSPONER) {
  try {
    localStorage.setItem(clavePospuesto(id), String(Date.now() + Math.max(1, dias) * 86_400_000));
  } catch { /* sin almacenamiento: el aviso volverá a salir, sin más */ }
}
